import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Trophy, TrendingUp, TrendingDown, Minus, Star, Clock, Sparkles,
  Zap, Users, Loader2, AlertTriangle, Home, Briefcase, BarChart3,
  ChevronRight, ArrowLeft
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'punctuality', label: 'Punctuality', icon: Clock },
  { key: 'cleaning_quality', label: 'Quality', icon: Sparkles },
  { key: 'professionalism', label: 'Professionalism', icon: Users },
  { key: 'efficiency', label: 'Efficiency', icon: Zap },
  { key: 'client_satisfaction', label: 'Satisfaction', icon: Star },
];

const TIER_STYLES = {
  Elite: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Strong: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  Watch: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Risk: 'bg-red-500/15 text-red-400 border-red-500/30',
  Unrated: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

const TIER_GRADIENTS = {
  Elite: 'from-emerald-600 to-emerald-800',
  Strong: 'from-blue-600 to-blue-800',
  Watch: 'from-amber-600 to-amber-800',
  Risk: 'from-red-600 to-red-800',
  Unrated: 'from-zinc-700 to-zinc-800',
};

function ScoreGauge({ pct, tier }) {
  const radius = 50;
  const circ = Math.PI * radius; // half circle
  const offset = circ - (pct / 100) * circ;
  const colors = { Elite: '#10b981', Strong: '#3b82f6', Watch: '#f59e0b', Risk: '#ef4444', Unrated: '#52525b' };
  return (
    <div className="relative w-36 h-20 mx-auto">
      <svg width="144" height="80" viewBox="0 0 144 80">
        <path d="M 12 72 A 60 60 0 0 1 132 72" fill="none" stroke="#27272a" strokeWidth="10" strokeLinecap="round" />
        <path d="M 12 72 A 60 60 0 0 1 132 72" fill="none" stroke={colors[tier] || '#52525b'}
          strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${circ}`} strokeDashoffset={offset}
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
        <p className="text-3xl font-black text-white text-center" data-testid="my-score-pct">{pct > 0 ? `${Math.round(pct)}%` : '—'}</p>
      </div>
    </div>
  );
}

export default function CleanerPerformancePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/performance/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('CleanerPerformancePage: failed to fetch performance data', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950">
        <p className="text-zinc-500 mb-4">Could not load performance data</p>
        <Button onClick={() => navigate('/cleaner/dashboard')} className="bg-zinc-800 text-white">Back</Button>
      </div>
    );
  }

  const isAlert = data.average_pct > 0 && data.average_pct < 75;

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="cleaner-performance-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white hover:bg-zinc-800 -ml-2"
            onClick={() => navigate('/cleaner/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <span className="font-bold text-white text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#F56F05]" /> My Performance
          </span>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Alert Banner */}
        {isAlert && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-0 bg-red-500/10 border border-red-500/20" data-testid="performance-alert">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Performance Below Target</p>
                  <p className="text-xs text-red-400/70 mt-0.5">
                    Your average is {data.average_pct}% (target: 75%). Focus on improving your weakest categories to raise your tier.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Main Score Card */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className={`border-0 bg-gradient-to-br ${TIER_GRADIENTS[data.tier] || TIER_GRADIENTS.Unrated} overflow-hidden shadow-lg`} data-testid="main-score-card">
            <CardContent className="p-6">
              <div className="text-center mb-2">
                <Badge className="bg-white/20 text-white border-white/30 text-xs">{data.tier} Tier</Badge>
              </div>
              <ScoreGauge pct={data.average_pct} tier={data.tier} />
              <div className="text-center mt-2">
                <p className="text-sm text-white/60">
                  {data.average_score}/25 avg from {data.total_scored_jobs} job{data.total_scored_jobs !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1 text-white/50 text-xs">
                  {data.trend === 'improving' && <><TrendingUp className="h-3.5 w-3.5 text-emerald-300" /> Trending Up</>}
                  {data.trend === 'declining' && <><TrendingDown className="h-3.5 w-3.5 text-red-300" /> Trending Down</>}
                  {data.trend === 'stable' && data.total_scored_jobs > 0 && <><Minus className="h-3.5 w-3.5" /> Stable</>}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Breakdown */}
        {data.total_scored_jobs > 0 && (
          <Card className="border-0 bg-zinc-900" data-testid="category-breakdown">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Category Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {CATEGORIES.map(cat => {
                const val = data.category_averages[cat.key] || 0;
                const pct = (val / 5) * 100;
                return (
                  <div key={cat.key}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <cat.icon className="h-3.5 w-3.5 text-zinc-500" />
                        <span className="text-xs text-zinc-300 font-medium">{cat.label}</span>
                      </div>
                      <span className="text-xs font-bold text-zinc-200">{val}/5</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Last 10 Scores */}
        {data.last_10_scores.length > 0 && (
          <div data-testid="score-history">
            <h2 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5 text-[#F56F05]" /> Recent Scores
            </h2>
            {/* Mini Chart */}
            <Card className="border-0 bg-zinc-900 mb-3">
              <CardContent className="p-4">
                <div className="flex items-end justify-between gap-1 h-16">
                  {data.last_10_scores.slice().reverse().map((s, i) => {
                    const h = Math.max(s.pct * 0.6, 6);
                    return (
                      <motion.div key={s.id || `slot-${i}`} className="flex-1 flex flex-col items-center gap-1"
                        initial={{ height: 0 }} animate={{ height: 'auto' }} transition={{ delay: i * 0.05 }}>
                        <div className={`w-full max-w-[20px] rounded-t ${
                          s.pct >= 90 ? 'bg-emerald-500' : s.pct >= 80 ? 'bg-blue-500' : s.pct >= 70 ? 'bg-amber-500' : 'bg-red-500'
                        }`} style={{ height: `${h}px` }} />
                      </motion.div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-zinc-600">Oldest</span>
                  <span className="text-[9px] text-zinc-600">Latest</span>
                </div>
              </CardContent>
            </Card>

            {/* Score List */}
            <div className="space-y-1.5">
              {data.last_10_scores.map(s => (
                <Card key={s.id} className="border-0 bg-zinc-900/60">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-zinc-300 font-medium truncate">{s.service_name || 'Cleaning Job'}</p>
                      <p className="text-[10px] text-zinc-600">{new Date(s.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-sm font-bold ${
                        s.pct >= 90 ? 'text-emerald-400' : s.pct >= 80 ? 'text-blue-400' : s.pct >= 70 ? 'text-amber-400' : 'text-red-400'
                      }`}>{s.total_score}/25</span>
                      <Badge className={`text-[10px] ${TIER_STYLES[s.pct >= 90 ? 'Elite' : s.pct >= 80 ? 'Strong' : s.pct >= 70 ? 'Watch' : 'Risk']}`}>
                        {s.pct}%
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Client Reviews */}
        {data.reviews?.length > 0 && (
          <div data-testid="client-reviews">
            <h2 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Star className="h-3.5 w-3.5 text-[#F56F05]" /> Client Reviews
            </h2>
            <div className="space-y-2">
              {data.reviews.map((r, i) => (
                <Card key={r.id || i} className="border-0 bg-zinc-900/60">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-0.5 mb-1.5">
                      {[...Array(5)].map((_, j) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Star key={j} className={`h-3 w-3 ${j < (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
                      ))}
                    </div>
                    {r.comment && <p className="text-xs text-zinc-300 leading-relaxed">{r.comment}</p>}
                    <p className="text-[10px] text-zinc-600 mt-1.5">{new Date(r.created_at).toLocaleDateString()}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {data.total_scored_jobs === 0 && (
          <Card className="border-0 bg-zinc-900" data-testid="no-scores">
            <CardContent className="p-8 text-center">
              <Trophy className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No scores yet</p>
              <p className="text-xs text-zinc-600 mt-1">Complete jobs to start building your performance profile</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-800/50" data-testid="bottom-nav">
        <div className="max-w-lg mx-auto flex items-center justify-around h-16 px-4">
          <Link to="/cleaner/dashboard" className="flex flex-col items-center gap-1 min-w-[60px]" data-testid="nav-home">
            <Home className="h-5 w-5 text-zinc-600" />
            <span className="text-[10px] font-medium text-zinc-600">Home</span>
          </Link>
          <Link to="/cleaner/dashboard" className="flex flex-col items-center gap-1 min-w-[60px]" data-testid="nav-jobs">
            <Briefcase className="h-5 w-5 text-zinc-600" />
            <span className="text-[10px] font-medium text-zinc-600">Jobs</span>
          </Link>
          <Link to="/cleaner/performance" className="flex flex-col items-center gap-1 min-w-[60px]" data-testid="nav-stats">
            <BarChart3 className="h-5 w-5 text-[#F56F05]" />
            <span className="text-[10px] font-medium text-[#F56F05]">Stats</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
